// FlixPlayerActionButton.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import NeedAuth from '../container/NeedAuth.js';

import numberWithCommas from '../resource/numberWithCommas.js';
import trackMixpanel, {
  EventTypes,
  ButtonId,
  getViewId,
} from '../resource/mixpanel.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import media from '../style/media.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import TextEllipsis from '../style/TextEllipsis.js';
import { textColor, color, loadingIconOpacity } from '../style/variables.js';

import DiamondIconResource from '../../img/point_2.svg';
import LoadingImageResource from '../../img/img_loading_white_20.svg';

export class FlixPlayerActionButton extends React.PureComponent {
  state = { price: null };
  trackUserClick = ({ buttonId }) => {
    const { badges, unlockPrice, messageId } = this.props;
    const abData = { unlockPrice };
    trackMixpanel({
      type: EventTypes.USER_CLICKED,
      payload: {
        viewId: getViewId({ pathname: window.location.pathname }),
        buttonId,
        messageId,
        abData,
        extraProps: {
          badges: badges?.join(','),
        },
      },
    });
  };
  handleClickLogin = () => {
    const { login } = this.props;
    return login();
  };
  handleClickAgeVerify = () => {
    const { addModal } = this.props;
    this.trackUserClick({
      buttonId: ButtonId.Detail.ButtonAgeVerifyFlixDetail,
    });
    return addModal({ id: 'VerifyFreeUserModal', isHigherThanAll: true });
  };
  handleClickUnlock = async () => {
    const {
      canViewMedia,
      messageId,
      drmFeatureName,
      sendUnlockMessage,
      replace,
    } = this.props;
    this.trackUserClick({ buttonId: ButtonId.All.ButtonUnlock });
    if (canViewMedia) {
      return replace(`/post/${messageId}`);
    }
    sendUnlockMessage({
      id: messageId,
      shouldCheckDrm: true,
      drmFeatureName,
    });
  };
  renderLoading = () => {
    return (
      <LoadingWrapper>
        <LoadingIcon src={LoadingImageResource} alt="Loading" />
      </LoadingWrapper>
    );
  };
  renderUnlockButtonContent = () => {
    const { isAuthed, canViewMedia, isAgeVerified, t, unlockPrice } =
      this.props;
    if (!isAuthed) {
      return (
        <ActionButtonText>
          {t('guest_post_video_detail_unlock', {
            ns: TranslationNamespace.POST,
          })}
        </ActionButtonText>
      );
    }
    if (!isAgeVerified && canViewMedia) {
      return (
        <ActionButtonText>
          {t('button_free_watch', {
            ns: TranslationNamespace.POST,
          })}
        </ActionButtonText>
      );
    }
    return (
      <>
        <ActionButtonText>
          {t('button_watch_full_video', {
            ns: TranslationNamespace.POST,
          })}
        </ActionButtonText>
        {!canViewMedia && (
          <>
            <Divider />
            <DiamondIcon src={DiamondIconResource} />
            {unlockPrice
              ? numberWithCommas({ number: unlockPrice })
              : unlockPrice}
          </>
        )}
      </>
    );
  };
  render() {
    const {
      isUnlocking,
      isAuthed,
      isPresenceUserChannelSubscribed,
      isDrmLicenseAvailabilityFetched,
      isAgeVerified,
      canViewMedia,
    } = this.props;
    const { price } = this.state;
    let unlockPrice = this.props.unlockPrice;
    if (price) {
      unlockPrice = price;
    }
    const shouldShowLoading =
      isUnlocking ||
      (isAuthed && !isPresenceUserChannelSubscribed) ||
      !isDrmLicenseAvailabilityFetched ||
      unlockPrice === null;

    return (
      <StyledFlixPlayerActionButton>
        <NeedAuth>
          <ActionButton
            data-element_id={
              canViewMedia
                ? ButtonId.Detail.ButtonFreeDetailSignup
                : ButtonId.Detail.ButtonDetailSignup
            }
            data-key="needAuth"
            isFilled
            onClick={() => {
              this.trackUserClick({
                buttonId: canViewMedia
                  ? ButtonId.Detail.ButtonFreeDetailSignup
                  : ButtonId.Detail.ButtonDetailSignup,
              });
              this.handleClickLogin();
            }}
          >
            {this.renderUnlockButtonContent()}
          </ActionButton>
          <ActionButton data-key="authing" disabled isFilled>
            {this.renderLoading()}
          </ActionButton>
          <ActionButton
            data-element_id={
              !isAgeVerified
                ? ButtonId.Detail.ButtonAgeVerifyFlixDetail
                : ButtonId.All.ButtonUnlock
            }
            data-key="authed"
            disabled={shouldShowLoading}
            onClick={() =>
              !isAgeVerified
                ? this.handleClickAgeVerify()
                : this.handleClickUnlock()
            }
          >
            {shouldShowLoading
              ? this.renderLoading()
              : this.renderUnlockButtonContent()}
          </ActionButton>
        </NeedAuth>
      </StyledFlixPlayerActionButton>
    );
  }
}

FlixPlayerActionButton.propTypes = {
  messageId: PropTypes.string,
  drmFeatureName: PropTypes.string,
  unlockPrice: PropTypes.number,
  isPresenceUserChannelSubscribed: PropTypes.bool,
  canViewMedia: PropTypes.bool,
  isUnlocking: PropTypes.bool,
  isAuthed: PropTypes.bool,
  isDrmLicenseAvailabilityFetched: PropTypes.bool,
  isAgeVerified: PropTypes.bool,
  badges: PropTypes.array,
  login: PropTypes.func,
  sendUnlockMessage: PropTypes.func,
  addModal: PropTypes.func,
  replace: PropTypes.func,
  t: PropTypes.func.isRequired,
};

FlixPlayerActionButton.defaultProps = {
  messageId: null,
  drmFeatureName: null,
  unlockPrice: null,
  isPresenceUserChannelSubscribed: false,
  canViewMedia: false,
  isUnlocking: null,
  isAuthed: false,
  isDrmLicenseAvailabilityFetched: false,
  isAgeVerified: false,
  badges: [],
  login: () => null,
  sendUnlockMessage: () => null,
  addModal: () => null,
  replace: () => null,
};

const StyledFlixPlayerActionButton = styled.div`
  width: 100%;
  max-width: 1200px;
`;

const ActionButton = styled.button.attrs(({ isFilled }) => ({
  style: {
    backgroundColor: isFilled ? color.tealBlue : 'rgba(25, 25, 25, 0.8)',
    color: isFilled ? color.neutral[0] : textColor.primary,
  },
}))`
  ${ResetButtonStyle};
  border-color: ${color.tealBlue};
  border-style: solid;
  border-width: 1px;
  border-radius: 4px;
  padding-right: 16px;
  padding-left: 16px;
  width: 100%;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 18px;
  font-weight: 600;
  ${media.mobile`
    height: 36px;
  `}
`;

const ActionButtonText = styled.div`
  ${TextEllipsis};
`;

const Divider = styled.div`
  margin-left: 8px;
  margin-right: 8px;
  width: 1px;
  height: 28px;
  background-color: ${color.tealBlue};
  ${media.mobile`
    height: 16px;
  `};
`;

const DiamondIcon = styled.img`
  margin-right: 4px;
  width: auto;
  height: 32px;
  ${media.mobile`
    height: 16px;
  `}
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${loadingIconOpacity};
`;

const LoadingIcon = styled.img`
  width: auto;
  height: 32px;
  ${media.mobile`
    height: 12px;
  `}
`;

export default withTranslation()(FlixPlayerActionButton);
